/**
 * One expense — §6.4, FR-9 through FR-15, FR-44, FR-67, FR-70, FR-71.
 *
 * This is where a machine reading becomes a person's decision, and the screen
 * is built around making that a single, informed act:
 *
 *   - The document is on screen next to what was read from it. Confirming a
 *     value without being able to see its source is not review.
 *   - When nothing needs a decision, one button files it (FR-70). The person
 *     still presses it: nothing is filed by the reader alone (FR-71).
 *   - What does need a decision is stated above the button, each with the one
 *     action that resolves it. A hard block has no override anywhere on the
 *     page (FR-59, TR-19); a flag takes a doctor's note or a written reason
 *     (FR-67); a possible duplicate takes an acknowledgement (FR-62).
 *   - Edits save as they are made, field by field, and only the field that
 *     changed is sent — so a save can never write back a stale copy of the
 *     fields it did not touch.
 */
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, uploadReceipt, ApiError } from '../api.js';
import { useApi, useAction } from '../hooks.js';
import {
  ErrorBox,
  Icon,
  Money,
  Notice,
  PageHead,
  Pill,
  Skeleton,
  Spinner,
  centsToInput,
  formatDate,
  inputToCents,
  toast,
} from '../ui.js';
import {
  CATEGORIES,
  DOC_TYPES,
  STATUS_LABEL,
  byPriority,
  categoryLabel,
  docLabel,
  sourceLabel,
} from '../labels.js';
import type {
  AppConfigResponse,
  Artifact,
  CoveredPerson,
  Expense,
  ExpenseDetailResponse,
} from '../types.js';

const LOW_CONFIDENCE = 0.75;
const MIN_REASON = 10;
type Patch = Partial<Expense>;

export function ExpenseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useApi<ExpenseDetailResponse>(id ? `/api/expenses/${id}` : null);
  const persons = useApi<{ persons: CoveredPerson[] }>('/api/persons');
  const config = useApi<AppConfigResponse>('/api/config');

  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState<Patch>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  // A different record is a different form.
  useEffect(() => {
    setEditing(false);
    setDirty({});
    setAcknowledged(false);
    setSaveError(null);
  }, [id]);

  // A receipt just uploaded has no record until the reader finishes. Wait for
  // it rather than reporting "not found".
  const stillReading = detail.error instanceof ApiError && detail.error.status === 404;
  const retries = useRef(0);
  useEffect(() => {
    if (!stillReading || retries.current >= 30) return;
    const t = setTimeout(() => {
      retries.current++;
      detail.reload();
    }, 4000);
    return () => clearTimeout(t);
  }, [stillReading, detail.reload, detail.error]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Send only what changed. Returns false if the save failed, so nothing that
   * depends on it (filing, above all) goes ahead on stale data.
   */
  const save = useCallback(
    async (patch: Patch, opts: { quiet?: boolean } = {}): Promise<boolean> => {
      if (Object.keys(patch).length === 0) return true;
      setSaveError(null);
      try {
        await api.patch(`/api/expenses/${id}`, patch);
        setDirty((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(patch)) delete (next as Record<string, unknown>)[key];
          return next;
        });
        detail.reload();
        if (!opts.quiet) toast('Saved');
        return true;
      } catch (err) {
        setSaveError(err);
        return false;
      }
    },
    [id, detail.reload], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const file = useAction(async () => {
    if (!(await save(dirty, { quiet: true }))) return;
    await api.post(`/api/expenses/${id}/verify`, { acknowledge_duplicate: acknowledged });
    // Straight on to the next one, if there is one.
    const established = config.data?.config.hsa_establishment_date ?? null;
    const queue = await api
      .get<{ items: Expense[] }>('/api/expenses?status=draft&limit=200')
      .then((r) => r.items.filter((e) => e.expense_id !== id).sort(byPriority(established)))
      .catch(() => []);
    if (queue.length > 0) {
      toast(`Filed. ${queue.length} left to review.`);
      navigate(`/expenses/${queue[0]!.expense_id}`);
    } else {
      toast('Filed. Nothing left to review.');
      navigate('/');
    }
  });

  if (stillReading) {
    return retries.current >= 30 ? (
      <Notice kind="warn" title="This receipt is taking a long time to read">
        The file is saved. Check Home in a few minutes.
      </Notice>
    ) : (
      <Spinner what="Reading this receipt" />
    );
  }
  if (detail.error) return <ErrorBox error={detail.error} />;
  if (!detail.data) return <Skeleton rows={4} />;

  const { expense, requirements, hard_blocks, duplicate_warning, warnings, suggestions, events } = detail.data;
  const people = (persons.data?.persons ?? []).filter((p) => p.active || p.person_id === expense.patient_id);
  const isDraft = expense.status === 'draft';
  const value = <K extends keyof Expense>(key: K): Expense[K] =>
    (key in dirty ? (dirty as Expense)[key] : expense[key]) as Expense[K];
  const unsure = (key: keyof Expense) => isDraft && (expense.field_confidence[key] ?? 1) < LOW_CONFIDENCE;

  const flagOpen = requirements.find((r) => r.id === 'required_override_reason');
  const attestationRequired = requirements.some((r) => r.id === 'required_attestation');
  const needsAttestation = attestationRequired || expense.attest_no_double_dip;
  // What still stands between this record and filing, in plain words. The
  // flag and the attestation have their own controls on the page.
  const outstanding = requirements.filter(
    (r) => r.id !== 'required_override_reason' && r.id !== 'required_attestation',
  );
  const blocked = hard_blocks.length > 0;
  const canFile =
    isDraft &&
    !blocked &&
    requirements.length === 0 &&
    (!duplicate_warning || acknowledged);

  const reload = () => detail.reload();

  return (
    <>
      <PageHead
        back={isDraft ? { to: '/', label: 'To review' } : { to: '/expenses', label: 'Expenses' }}
        title={
          <>
            {expense.provider_name ?? 'Unread receipt'}{' '}
            <Pill tone={expense.status === 'verified' ? 'ok' : undefined}>{STATUS_LABEL[expense.status]}</Pill>
          </>
        }
      />

      <div className="review">
        <DocPane expense={expense} onChange={reload} suggestions={suggestions.map((s) => s.message)} />

        <div className="stack">
          {/* ---- Tier 1. No override control appears anywhere near these. ---- */}
          {hard_blocks.map((b) => (
            <Notice key={b.rule} kind="stop" title="This cannot be claimed as it stands">
              {b.message} {b.remedy}
            </Notice>
          ))}

          {/* Once a person has filled in what the reader could not, there is
              nothing left to say about the failed read. */}
          {expense.extraction_error && isDraft && (!expense.date_of_service || expense.amount_claimed_cents === null) ? (
            <Notice title="This one could not be read">
              The file is saved. Enter the date, amount and person from the receipt.
            </Notice>
          ) : null}

          {duplicate_warning && isDraft ? (
            <Notice kind="warn" title="Possible duplicate">
              {duplicate_warning.message}
              <div className="actions">
                {duplicate_warning.expense_ids.map((other, i) => (
                  <Link className="btn" key={other} to={`/expenses/${other}`}>
                    {duplicate_warning.expense_ids.length === 1 ? 'Open the other record' : `Open record ${i + 1}`}
                  </Link>
                ))}
              </div>
              <div className="check-row">
                <input
                  id="dup"
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <label htmlFor="dup">These are separate expenses.</label>
              </div>
            </Notice>
          ) : null}

          {warnings.map((w) => (
            <Notice key={w.rule} kind="warn" title="Check the amount">
              {w.message}
            </Notice>
          ))}

          {isDraft && flagOpen ? <Flag expense={expense} onChange={reload} /> : null}
          {expense.qual_verdict === 'overridden' && expense.qual_override_reason ? (
            <Notice kind="ok" title="Your reason is on the record">
              {expense.qual_override_reason}
            </Notice>
          ) : null}

          {!isDraft ? <StatusNote expense={expense} events={events} /> : null}

          {/* ---- What was read ------------------------------------------------ */}
          {editing && isDraft ? (
            <EditForm
              expense={expense}
              people={people}
              dirty={dirty}
              setDirty={setDirty}
              save={save}
              unsure={unsure}
              onDone={() => setEditing(false)}
            />
          ) : (
            <>
              <div className="facts">
                <Fact label="Paid to" value={value('provider_name')} unsure={unsure('provider_name')} />
                <Fact
                  label="Date"
                  value={value('date_of_service') ? formatDate(value('date_of_service'), { year: 'always' }) : null}
                  unsure={unsure('date_of_service')}
                  required={isDraft}
                />
                <Fact
                  label={value('insurance_billed') ? 'You owed' : 'You paid'}
                  value={value('amount_claimed_cents') === null ? null : <Money cents={value('amount_claimed_cents')} />}
                  unsure={unsure('amount_claimed_cents')}
                  required={isDraft}
                />
                <Fact label="For" value={value('service_description')} unsure={unsure('service_description')} />
                {!isDraft ? (
                  <Fact
                    label="Person"
                    value={people.find((p) => p.person_id === expense.patient_id)?.display_name ?? null}
                  />
                ) : null}
                {expense.source !== 'manual' ? <Fact label="From" value={sourceLabel(expense.source)} /> : null}
              </div>
              {/* FR-9 — a machine reading is marked as one until a person
                  confirms it. */}
              {isDraft &&
              !expense.extraction_error &&
              (expense.extraction_method === 'textract' || expense.extraction_method === 'vision_fallback') ? (
                <p className="xs muted">
                  Read from the document by the app. Check it against the image; nothing is filed until you
                  press Looks right.
                </p>
              ) : null}
              {/* On a record in review these are under "Edit details". */}
              {!isDraft ? <RecordDetails expense={expense} /> : null}

              {isDraft ? (
                <div className="field">
                  <span className="label">Who was it for?</span>
                  {!persons.data ? null : people.length === 0 ? (
                    <div className="actions">
                      <span className="small muted">No one is set up yet.</span>
                      <Link className="btn" to="/settings#family">
                        Add your family
                      </Link>
                    </div>
                  ) : (
                    <div className="chips">
                      {people.map((p) => (
                        <button
                          key={p.person_id}
                          className="chip"
                          aria-pressed={value('patient_id') === p.person_id}
                          onClick={() => void save({ patient_id: p.person_id }, { quiet: true })}
                        >
                          {p.display_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}

          {isDraft && value('insurance_billed') === true ? (
            <Insurance expense={expense} onChange={reload} />
          ) : null}

          {isDraft && needsAttestation ? (
            <div className="check-row">
              <input
                id="attest"
                type="checkbox"
                checked={value('attest_no_double_dip') ?? false}
                onChange={(e) => void save({ attest_no_double_dip: e.target.checked }, { quiet: true })}
              />
              <label htmlFor="attest">
                This expense was not paid by insurance or another plan, and was not deducted on
                Schedule A.{' '}
                <span className="muted">
                  {attestationRequired
                    ? 'Asked because of your answers for this tax year.'
                    : expense.attest_at
                      ? `Confirmed ${formatDate(expense.attest_at, { year: 'always' })}.`
                      : ''}
                </span>
              </label>
            </div>
          ) : null}

          {saveError ? <ErrorBox error={saveError} /> : null}
          {file.error ? <FileError error={file.error} /> : null}

          {/* ---- The decision ------------------------------------------------- */}
          {isDraft ? (
            <>
              {outstanding.length > 0 && !blocked ? (
                <ul className="small muted" style={{ margin: 0, paddingLeft: 20 }}>
                  {outstanding.map((r) => (
                    <li key={r.id}>{r.message}</li>
                  ))}
                </ul>
              ) : null}
              {!blocked ? (
                <div className="decide">
                  <button className="primary big" disabled={!canFile || file.pending} onClick={() => void file.run()}>
                    {file.pending ? 'Filing…' : 'Looks right'}
                  </button>
                </div>
              ) : null}
              <div className="actions center">
                {!editing ? (
                  <button className="quiet" onClick={() => setEditing(true)}>
                    Edit details
                  </button>
                ) : null}
                <button
                  className="quiet"
                  onClick={() => void save({ insurance_billed: value('insurance_billed') === true ? false : true }, { quiet: true })}
                >
                  {value('insurance_billed') === true ? 'No insurance involved' : 'Insurance paid part'}
                </button>
              </div>
            </>
          ) : null}

          <Dispose expense={expense} onChange={reload} />
        </div>
      </div>

      <details className="more">
        <summary>History · {events.length} entr{events.length === 1 ? 'y' : 'ies'}</summary>
        <div className="list">
          {events.map((e, i) => (
            <div className="item plain" key={i}>
              <span>
                <div className="title" style={{ fontWeight: 500 }}>
                  {eventLabel(e.kind, e.from_status, e.to_status)}
                </div>
                <div className="sub">
                  {e.actor}
                  {e.reason ? ` · ${e.reason}` : ''}
                </div>
              </span>
              <span className="end sub">{formatDate(e.at, { year: 'always' })}</span>
            </div>
          ))}
        </div>
      </details>
    </>
  );
}

// ---------------------------------------------------------------------------

function Fact({
  label,
  value,
  unsure,
  required,
}: {
  label: string;
  value: ReactNode;
  unsure?: boolean;
  required?: boolean;
}) {
  const missing = value === null || value === undefined || value === '';
  if (missing && !required) return null;
  return (
    <div className="fact">
      <span className="k">{label}</span>
      <span className={`v${missing ? ' missing' : ''}`}>
        {missing ? 'Needed' : value}
        {/* FR-44 — where the reader was unsure, say so next to the value. */}
        {unsure && !missing ? <span className="check">check</span> : null}
      </span>
    </div>
  );
}

/**
 * Fields the form stopped asking for. They stay on every record that has them
 * and are shown here; on a record waiting for review they are editable under
 * "Edit details".
 */
function RecordDetails({ expense }: { expense: Expense }) {
  const rows: [string, ReactNode][] = [
    ['Date paid', expense.date_paid ? formatDate(expense.date_paid, { year: 'always' }) : null],
    ['Paid with', expense.payment_method],
    ['Type', expense.category && expense.category !== 'uncategorized' ? categoryLabel(expense.category) : null],
    ['Notes', expense.notes],
  ];
  const present = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (present.length === 0) return null;
  return (
    <details className="more">
      <summary>More details</summary>
      <div className="facts">
        {present.map(([label, v]) => (
          <Fact key={label} label={label} value={v} />
        ))}
      </div>
    </details>
  );
}

/** The document, beside the values read from it. */
function DocPane({
  expense,
  onChange,
  suggestions,
}: {
  expense: Expense;
  onChange: () => void;
  suggestions: string[];
}) {
  const docs = expense.artifacts;
  const [selected, setSelected] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [type, setType] = useState('receipt');
  const fileRef = useRef<HTMLInputElement>(null);

  const current = docs.find((a) => a.sha256 === selected) ?? docs[0] ?? null;
  const isImage = current ? current.content_type.startsWith('image/') : false;
  const isPdf = current ? current.content_type === 'application/pdf' : false;

  // Links to the archive last five minutes, so one is minted when a document
  // is shown and again when it is opened, never stored.
  useEffect(() => {
    setUrl(null);
    setFailed(false);
    if (!current) return;
    let cancelled = false;
    api
      .get<{ url: string }>(`/api/expenses/${expense.expense_id}/artifacts/${current.sha256}/url?inline=1`)
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [expense.expense_id, current?.sha256]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (artifact: Artifact) => {
    const res = await api.get<{ url: string }>(
      `/api/expenses/${expense.expense_id}/artifacts/${artifact.sha256}/url?inline=1`,
    );
    window.open(res.url, '_blank', 'noopener');
  };

  const attach = useAction(async (picked: File, as: string) => {
    const { key } = await uploadReceipt(picked, { expenseId: expense.expense_id });
    await api.post(`/api/expenses/${expense.expense_id}/artifacts`, { type: as, s3_key: key });
    toast('Attached');
    onChange();
  });

  const editable = expense.status !== 'void' && expense.status !== 'reimbursed';

  return (
    <div className="doc-pane">
      <div className="doc-frame">
        {!current ? (
          <div className="doc-empty">
            <Icon name={expense.source === 'mileage' ? 'car' : expense.source === 'amazon_import' ? 'cart' : 'doc'} />
            {expense.source === 'mileage'
              ? 'A mileage entry is its own record. No document is needed.'
              : expense.source === 'amazon_import'
                ? 'The Amazon order line is the record. The export it came from is kept in the archive.'
                : 'No document attached yet.'}
          </div>
        ) : isImage && url && !failed ? (
          <img src={url} alt={`${docLabel(current.type)}: ${current.filename}`} onError={() => setFailed(true)} />
        ) : isPdf && url && !failed ? (
          // Most statements and every Amazon invoice are PDFs. The browser's own
          // viewer shows them in place; "Open file" below is the full view.
          <iframe className="doc-pdf" src={url} title={`${docLabel(current.type)}: ${current.filename}`} />
        ) : (
          <div className="doc-empty">
            <Icon name="doc" />
            <span>
              {docLabel(current.type)} · {current.filename}
            </span>
            <button onClick={() => void open(current)}>Open the file</button>
          </div>
        )}
      </div>

      {current ? (
        <div className="row-wrap">
          {docs.length > 1
            ? docs.map((a) => (
                <button
                  key={a.sha256}
                  className="chip"
                  aria-pressed={current.sha256 === a.sha256}
                  onClick={() => setSelected(a.sha256)}
                  title={a.filename}
                >
                  {docLabel(a.type)}
                </button>
              ))
            : <span className="xs muted">{docLabel(current.type)}</span>}
          {(isImage || isPdf) && !failed ? (
            <button className="quiet" onClick={() => void open(current)}>
              Open file
            </button>
          ) : null}
        </div>
      ) : null}

      {suggestions.map((s) => (
        <p className="xs muted" key={s}>
          {s}
        </p>
      ))}

      {editable ? (
        <details className="more">
          <summary>Add a document</summary>
          <div className="stack tight">
            <select aria-label="Kind of document" value={type} onChange={(e) => setType(e.target.value)}>
              {DOC_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void attach.run(f, type);
                e.target.value = '';
              }}
            />
            <button onClick={() => fileRef.current?.click()} disabled={attach.pending}>
              <Icon name="upload" />
              {attach.pending ? 'Uploading…' : 'Choose a file'}
            </button>
            {attach.error ? <ErrorBox error={attach.error} /> : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/**
 * The full form, for when the reading was wrong or incomplete.
 *
 * Three things are needed to file: the date, the person, the amount. The rest
 * is kept when the reader finds it and can be corrected here, but none of it
 * blocks. Date paid, payment method and type sit under "More details": they
 * stay on the record, and nothing asks for them.
 */
function EditForm({
  expense,
  people,
  dirty,
  setDirty,
  save,
  unsure,
  onDone,
}: {
  expense: Expense;
  people: CoveredPerson[];
  dirty: Patch;
  setDirty: (fn: (prev: Patch) => Patch) => void;
  save: (patch: Patch) => Promise<boolean>;
  unsure: (key: keyof Expense) => boolean;
  onDone: () => void;
}) {
  const current = <K extends keyof Expense>(key: K): Expense[K] =>
    (key in dirty ? (dirty as Expense)[key] : expense[key]) as Expense[K];
  const set = (patch: Patch) => setDirty((prev) => ({ ...prev, ...patch }));
  /** Save one field when the person leaves it, if it changed. */
  const commit = (key: keyof Expense) => {
    if (!(key in dirty)) return;
    const next = (dirty as Expense)[key];
    if (next === expense[key]) {
      setDirty((prev) => {
        const rest = { ...prev };
        delete (rest as Record<string, unknown>)[key];
        return rest;
      });
      return;
    }
    void save({ [key]: next } as Patch);
  };
  const text = (key: 'provider_name' | 'service_description' | 'payment_method' | 'notes') => ({
    value: current(key) ?? '',
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      set({ [key]: e.target.value === '' ? null : e.target.value } as Patch),
    onBlur: () => commit(key),
  });

  return (
    <div className="card stack">
      <div className="fields two">
        <div className="field">
          <label htmlFor="dos">Date of service</label>
          <input
            id="dos"
            type="date"
            className={unsure('date_of_service') ? 'unsure' : undefined}
            value={current('date_of_service') ?? ''}
            onChange={(e) => set({ date_of_service: e.target.value || null })}
            onBlur={() => commit('date_of_service')}
          />
          <span className="hint">When the care or item was received.</span>
        </div>
        <div className="field">
          <label htmlFor="amount">{current('insurance_billed') ? 'What you owed' : 'What you paid'}</label>
          <AmountInput
            id="amount"
            className={unsure('amount_claimed_cents') ? 'unsure' : undefined}
            cents={current('amount_claimed_cents')}
            onChange={(cents) => set({ amount_claimed_cents: cents })}
            onBlur={() => commit('amount_claimed_cents')}
          />
          <span className="hint">Your share only, after any insurance.</span>
        </div>
      </div>

      <div className="field">
        <label htmlFor="patient">Who it was for</label>
        <select
          id="patient"
          value={current('patient_id') ?? ''}
          onChange={(e) => void save({ patient_id: e.target.value || null })}
        >
          <option value="">Choose…</option>
          {people.map((p) => (
            <option key={p.person_id} value={p.person_id}>
              {p.display_name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="provider">Paid to</label>
        <input id="provider" className={unsure('provider_name') ? 'unsure' : undefined} {...text('provider_name')} />
      </div>

      <div className="field">
        <label htmlFor="description">What it was for</label>
        <input id="description" {...text('service_description')} />
        <span className="hint">Only needed when the document does not say.</span>
      </div>

      <details className="more">
        <summary>More details</summary>
        <div className="fields">
          <div className="fields two">
            <div className="field">
              <label htmlFor="paid">Date paid</label>
              <input
                id="paid"
                type="date"
                value={current('date_paid') ?? ''}
                onChange={(e) => set({ date_paid: e.target.value || null })}
                onBlur={() => commit('date_paid')}
              />
              <span className="hint">Left empty, the date of service is used.</span>
            </div>
            <div className="field">
              <label htmlFor="payment">Paid with</label>
              <input id="payment" placeholder="Visa •4412" {...text('payment_method')} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="category">Type of expense</label>
            <select
              id="category"
              value={current('category') ?? ''}
              onChange={(e) => void save({ category: e.target.value || null })}
            >
              <option value="">Not set</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            {expense.classification_note ? <span className="hint">{expense.classification_note}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="notes">Notes</label>
            <textarea id="notes" {...text('notes')} />
          </div>
        </div>
      </details>

      <div className="actions end">
        <button onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

/** A money field that keeps what was typed while it is being typed. */
function AmountInput({
  id,
  cents,
  onChange,
  onBlur,
  className,
}: {
  id: string;
  cents: number | null;
  onChange: (cents: number | null) => void;
  onBlur: () => void;
  className?: string;
}) {
  const [raw, setRaw] = useState(centsToInput(cents));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setRaw(centsToInput(cents));
  }, [cents]);
  return (
    <input
      id={id}
      className={className}
      inputMode="decimal"
      placeholder="0.00"
      value={raw}
      onFocus={() => (focused.current = true)}
      onChange={(e) => {
        setRaw(e.target.value);
        onChange(inputToCents(e.target.value));
      }}
      onBlur={() => {
        focused.current = false;
        setRaw(centsToInput(inputToCents(raw)));
        onBlur();
      }}
    />
  );
}

/**
 * FR-65 / FR-67 — a flag, and the two ways to answer it.
 *
 * A dual-purpose item is settled by a doctor's note or by a written reason. A
 * not-qualified flag is settled only by a written reason. Either is stored on
 * the record and printed in the audit packet.
 */
function Flag({ expense, onChange }: { expense: Expense; onChange: () => void }) {
  const [reason, setReason] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const dual = expense.qual_verdict === 'dual_purpose';

  const override = useAction(async () => {
    await api.post(`/api/expenses/${expense.expense_id}/override`, { reason });
    toast('Reason saved');
    onChange();
  });
  const attachNote = useAction(async (picked: File) => {
    const { key } = await uploadReceipt(picked, { expenseId: expense.expense_id });
    await api.post(`/api/expenses/${expense.expense_id}/artifacts`, { type: 'lmn', s3_key: key });
    toast('Note attached');
    onChange();
  });

  return (
    <Notice kind="warn" title={dual ? 'This can be personal or medical' : 'This may not qualify'}>
      <div className="stack tight">
        <span>{expense.qual_rationale}</span>
        {dual ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void attachNote.run(f);
                e.target.value = '';
              }}
            />
            <div className="actions">
              <button onClick={() => fileRef.current?.click()} disabled={attachNote.pending}>
                <Icon name="upload" />
                {attachNote.pending ? 'Uploading…' : 'Attach a doctor’s note'}
              </button>
            </div>
            <span className="xs muted">A letter, a prescription, or a visit note that names the condition.</span>
          </>
        ) : null}
        <div className="field">
          <label htmlFor="reason">{dual ? 'Or say why it is medical' : 'To claim it anyway, say why'}</label>
          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Recommended by Dr. … for …"
          />
        </div>
        <div className="actions">
          <button onClick={() => void override.run()} disabled={override.pending || reason.trim().length < MIN_REASON}>
            Save reason
          </button>
        </div>
        {override.error ? <ErrorBox error={override.error} /> : null}
        {attachNote.error ? <ErrorBox error={attachNote.error} /> : null}
      </div>
    </Notice>
  );
}

/** FR-13 — insurance paid part: the claim is what was owed, with a document that shows it. */
function Insurance({ expense, onChange }: { expense: Expense; onChange: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const hasStatement = expense.artifacts.some((a) => a.type === 'eob');
  const attach = useAction(async (picked: File) => {
    const { key } = await uploadReceipt(picked, { expenseId: expense.expense_id });
    await api.post(`/api/expenses/${expense.expense_id}/artifacts`, { type: 'eob', s3_key: key });
    toast('Statement attached');
    onChange();
  });
  return (
    <Notice kind={hasStatement ? 'ok' : 'info'} title="Insurance paid part of this">
      <div className="stack tight">
        <span>
          Claim only what you owed after insurance.{' '}
          {hasStatement
            ? 'The insurance statement is attached.'
            : 'Attach the Explanation of Benefits, or a provider statement that shows the insurance adjustment.'}
        </span>
        {!hasStatement ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void attach.run(f);
                e.target.value = '';
              }}
            />
            <div className="actions">
              <button onClick={() => fileRef.current?.click()} disabled={attach.pending}>
                <Icon name="upload" />
                {attach.pending ? 'Uploading…' : 'Attach the statement'}
              </button>
            </div>
          </>
        ) : null}
        {attach.error ? <ErrorBox error={attach.error} /> : null}
      </div>
    </Notice>
  );
}

function StatusNote({ expense, events }: { expense: Expense; events: ExpenseDetailResponse['events'] }) {
  const last = [...events].reverse().find((e) => e.to_status === expense.status && e.from_status !== e.to_status);
  const when = last ? ` on ${formatDate(last.at, { year: 'always' })}` : '';
  if (expense.status === 'verified') {
    return (
      <Notice kind="ok" title={`Filed${when}`}>
        Counted in the amount ready to claim.
      </Notice>
    );
  }
  if (expense.status === 'reimbursed') {
    return (
      <Notice
        title={`Reimbursed${when}`}
        actions={
          <Link className="btn" to="/withdrawals">
            Open Withdrawals
          </Link>
        }
      >
        Matched to a withdrawal. To change it, unmatch it there first.
      </Notice>
    );
  }
  return (
    <Notice title={`Not claiming${when}`}>
      {last?.reason ?? 'Kept in the archive and left out of the total.'}
    </Notice>
  );
}

/**
 * One action, two reasons.
 *
 * "Not a qualified expense" keeps the record as excluded (FR-68); "a duplicate
 * or a mistake" voids it. Both stay in the archive. The distinction matters to
 * the audit trail, so it is asked as a choice rather than as two buttons whose
 * names mean nothing to the person pressing them.
 */
function Dispose({ expense, onChange }: { expense: Expense; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState<'exclude' | 'void'>('exclude');
  const [note, setNote] = useState('');

  const dispose = useAction(async () => {
    const label = why === 'exclude' ? 'Not a qualified medical expense' : 'A duplicate, or added by mistake';
    const reason = note.trim() ? `${label}: ${note.trim()}` : `${label}.`;
    await api.post(`/api/expenses/${expense.expense_id}/${why}`, { reason });
    toast('Marked as not claiming');
    setOpen(false);
    onChange();
  });
  const reinstate = useAction(async () => {
    await api.post(`/api/expenses/${expense.expense_id}/reinstate`, { reason: 'Put back for review.' });
    toast('Back in review');
    onChange();
  });

  if (expense.status === 'reimbursed' || expense.status === 'void') return null;

  if (expense.status === 'excluded') {
    return (
      <div className="actions center">
        <button className="quiet" onClick={() => void reinstate.run()} disabled={reinstate.pending}>
          Claim this after all
        </button>
        {reinstate.error ? <ErrorBox error={reinstate.error} /> : null}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="actions center">
        <button className="quiet muted" onClick={() => setOpen(true)}>
          Don’t claim this
        </button>
      </div>
    );
  }

  return (
    <div className="card stack tight">
      <h3>Why not?</h3>
      <div className="check-row">
        <input id="why-exclude" type="radio" name="why" checked={why === 'exclude'} onChange={() => setWhy('exclude')} />
        <label htmlFor="why-exclude">It is not a qualified medical expense</label>
      </div>
      <div className="check-row">
        <input id="why-void" type="radio" name="why" checked={why === 'void'} onChange={() => setWhy('void')} />
        <label htmlFor="why-void">It is a duplicate, or was added by mistake</label>
      </div>
      <div className="field">
        <label htmlFor="why-note">Note (optional)</label>
        <input id="why-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <p className="xs muted">The record and its documents are kept. It is left out of the total.</p>
      {dispose.error ? <ErrorBox error={dispose.error} /> : null}
      <div className="actions end">
        <button onClick={() => setOpen(false)}>Cancel</button>
        <button className="danger" onClick={() => void dispose.run()} disabled={dispose.pending}>
          Don’t claim
        </button>
      </div>
    </div>
  );
}

function FileError({ error }: { error: unknown }) {
  if (error instanceof ApiError && (error.isHardBlock || error.isMissingRequirement)) {
    return (
      <Notice kind={error.isHardBlock ? 'stop' : 'warn'} title="Not filed yet">
        {error.message} {error.remedy ?? ''}
      </Notice>
    );
  }
  return <ErrorBox error={error} />;
}

function eventLabel(kind: string, from: string | null, to: string | null): string {
  if (kind === 'created') return 'Added';
  if (kind === 'artifact_added') return 'Document attached';
  if (kind === 'override') return 'Reason recorded';
  if (kind === 'ai_pass') return 'Read by the app';
  if (kind === 'status_change' && to) {
    if (to === 'verified' && from === 'reimbursed') return 'Unmatched from a withdrawal';
    if (to === 'verified') return 'Filed';
    if (to === 'reimbursed') return 'Matched to a withdrawal';
    if (to === 'excluded' || to === 'void') return 'Marked as not claiming';
    if (to === 'draft') return 'Put back for review';
  }
  return 'Edited';
}

